/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { Fragment, useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import { Button } from '@styles/button.style'
import { PopularSearchTermWrap } from '@components/PopularSearchTerm/index.style'
import PopupUtils from '@utils/popup-utils'
import useRootStore from '@store/useRootStore'
import classNames from 'classnames'
import { isMobile } from 'react-device-detect'
import { useOnMount, useOnUnmount } from '@utils/reactExt'
import _ from 'lodash'
import { Schema$PopularSearchTerm } from '@protocol/popularSearchTerm'
import { POPULAR_SEARCH_TERM_OPTION_KEY } from '@v2/constants/popular-search-term'
import { PopupCloseType } from '@views/Popup/General'

interface PopularSearchTermProps {
  className?: string
  showInChat?: boolean
  isScrollCloseEnabled?: boolean // 스크롤 시 닫히는 기능에 대한 불린값 (기본값: T)
}

export const PopularSearchTerm: React.FC<PopularSearchTermProps> = observer((props) => {
  const { className, showInChat = false, isScrollCloseEnabled = true } = props
  const rollingTimerRef = useRef(null)
  const { globalStore, popularSearchTermStore, logStore } = useRootStore()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isOpen, setIsOpen] = useState(false)
  const [panelHeight, setPanelHeight] = useState(0)
  const { popularSearchTerms, isResetRollingIndex } = popularSearchTermStore

  const options = globalStore.getPopularSearchTermOptions()
  const rollingEnabled = options[POPULAR_SEARCH_TERM_OPTION_KEY.TERM_ROLLING_ENABLED]
  const rollingSecond = options[POPULAR_SEARCH_TERM_OPTION_KEY.TERM_ROLLING_SECOND]

  const popularSearchTermWrapClassName = classNames('', {
    'active': !isOpen && rollingEnabled,
    'pc': !isMobile,
    'is-open': isOpen,
    [className!]: className,
  })

  useOnMount(() => {
    popularSearchTermStore.fetchPopularSearchTerms().catch(console.error)
    if (isMobile && isScrollCloseEnabled) {
      window.addEventListener('scroll', scrollHandler, true)
    }
  })

  useOnUnmount(() => {
    clearTimeout(rollingTimerRef.current)
    if (isMobile && isScrollCloseEnabled) {
      window.removeEventListener('scroll', scrollHandler, true)
    }
  })

  useEffect(() => {
    if (!rollingEnabled) {
      clearTimeout(rollingTimerRef.current)
      setCurrentIndex(0)
      return
    }

    if (isOpen) {
      clearTimeout(rollingTimerRef.current)
      return
    }
    if (rollingEnabled) {
      rollingTimerRef.current = setTimeout(rollingTask, rollingSecond * 1000)
    }
  }, [isOpen, rollingEnabled])

  useEffect(() => {
    if (isResetRollingIndex) {
      popularSearchTermStore.setIsResetRollingIndex(false)
      setCurrentIndex(0)
    }
    document.documentElement.style.setProperty('--rolling-second', `${rollingSecond}s`)

    if (isOpen) {
      setPanelHeight(20 + (_.size(popularSearchTerms) * 52))
    } else {
      setPanelHeight(0)
    }

  }, [isResetRollingIndex, popularSearchTerms, isOpen])

  const scrollHandler = () => {
    setIsOpen(false)
  }

  const rollingTask = (): void => {
    const options = globalStore.getPopularSearchTermOptions()
    const rollingEnabled = options[POPULAR_SEARCH_TERM_OPTION_KEY.TERM_ROLLING_ENABLED]
    const rollingSecond = options[POPULAR_SEARCH_TERM_OPTION_KEY.TERM_ROLLING_SECOND]

    if (isOpen) {
      return
    }

    if (!rollingEnabled) {
      return
    }

    const { popularSearchTerms } = popularSearchTermStore
    setCurrentIndex((prevIndex) => {
      return (prevIndex + 1) % _.size(popularSearchTerms)
    })

    rollingTimerRef.current = setTimeout(rollingTask, rollingSecond * 1000)
  }

  const onChangePanel = (key: string): void => {
    if (key.length > 0) {
      // 이슈 키워드 헤드라인 상세 펼침 버튼 로그
      if (showInChat) {
        logStore.writeButtonLog({ pageId: 'h010', buttonId: '007' }).catch(console.error)
      } else {
        logStore.writeButtonLog({ pageId: 'b010', buttonId: '011' }).catch(console.error)
      }

      setIsOpen(prevIsOpen => !prevIsOpen)
    }
  }

  const onClickPopularSearchTermBtn = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    item: Schema$PopularSearchTerm,
  ): void => {
    e.preventDefault()
    e.stopPropagation()

    if (isOpen) {
      // 실시간 인기 검색어 특정 텍스트 버튼 로그
      if (showInChat) {
        logStore.writeButtonLog({ pageId: 'h010', buttonId: '008' }).catch(console.error)
      } else {
        logStore.writeButtonLog({ pageId: 'b010', buttonId: '012' }).catch(console.error)
      }
    } else {
      // 실시간 인기 검색어 헤드 텍스트 버튼 로그
      if (showInChat) {
        logStore.writeButtonLog({ pageId: 'h010', buttonId: '006' }).catch(console.error)
      } else {
        logStore.writeButtonLog({ pageId: 'b010', buttonId: '010' }).catch(console.error)
      }
    }
    PopupUtils.showPopularSearchTermPopup(item, (closeType: PopupCloseType): void => {
      if (closeType?.isTitleClose) {
        logStore.writeButtonLog({ pageId: 'j010', buttonId: '001' }).catch(console.error)
      } else if (closeType?.isFloatingClose) {
        logStore.writeButtonLog({ pageId: 'j010', buttonId: '003' }).catch(console.error)
      }
    })
  }

  const headerContents = (item: Schema$PopularSearchTerm): JSX.Element => {
    if (_.isEmpty(popularSearchTerms)) {
      return (<Fragment/>)
    }

    const index = _.findIndex(popularSearchTerms, popularSearchTerm => popularSearchTerm.id === item.id)
    return (
      <Button
        TP
        key={item.id}
        className="popular-searches-btn"
        onClick={(e) => onClickPopularSearchTermBtn(e, item)}
      >
        <span className="popular-searches-btn-num">{index + 1}</span> {item.term}
      </Button>
    )
  }

  const popularSearchTerm = popularSearchTerms?.[currentIndex]
  if (!popularSearchTerm) {
    return (<Fragment/>)
  }

  return (
    <PopularSearchTermWrap className={popularSearchTermWrapClassName}>
      <div className="popular-search-panel-header">
        <span className="head-icon-txt">now</span>
        {isOpen ? '이슈 키워드' : headerContents(popularSearchTerm)}
        <Button
          TP
          onClick={() => onChangePanel(popularSearchTerm.id.toString())}
          className="arrow-btn"
        >
          <span className={isOpen ? 'arrow-ico open' : 'arrow-ico'}/>
        </Button>
      </div>
      <div
        key={popularSearchTerm.id}
        className={isOpen ? 'popular-searches-panel active' : 'popular-searches-panel'}
        style={{ height: `${panelHeight}px`, transition: 'none' }}
      >
        {isOpen && (
          _.map(popularSearchTerms, (item, index) => (
            <Button
              TP
              key={item.id}
              className="popular-searches-panel-list"
              onClick={(e) => onClickPopularSearchTermBtn(e, item)}
            >
              <span className="popular-searches-panel-list-num">{index + 1}</span> {item.term}
            </Button>
          ))
        )}
      </div>
    </PopularSearchTermWrap>
  )
})
